// All texts in English. Must have exactly the same keys and {placeholders} as nl.ts (enforced by the type and by
// i18n.test.ts). Proper names of streets, shops, customers and cats come from the map and stay Dutch on purpose.
import type { MessageKey } from './nl';

export const en: Record<MessageKey, string> = {
  // --- Page ---
  'meta.title': 'Bak en Bezorg',
  'meta.description': 'Deliver fresh baked goods on time, warm and in one piece through the city. A game by Team O.',
  'meta.ogDescription': 'On time, warm and in one piece. A delivery game by Team O.',
  'canvas.aria': 'The city, with your scooter in the middle',
  'credit': 'Made by Team O • © 2026',

  // --- Common words ---
  'count.stars_one': '{n} star',
  'count.stars_other': '{n} stars',
  'list.and': ' and ',
  'common.done': 'Done',
  'common.on': 'On',
  'common.off': 'Off',

  // --- HUD ---
  'hud.clockTitle': 'Your shift clock',
  'hud.earnedSr': 'Earned this shift:',
  'hud.walletTitle': 'Your wallet',
  'hud.walletSr': 'In your wallet:',
  'hud.pause': 'Pause',
  'hud.offers': 'New orders',
  'hud.cargo': 'In your box',
  'hud.guideSkip': 'Skip the tutorial',
  'hud.thumbZone': 'Drag your thumb in the direction you want to ride',
  'hud.thumbHint': 'Put your thumb down and drag where you want to go',
  'hud.joystick': 'Steering: drag in the direction you want to ride',
  'hud.left': 'Steer left',
  'hud.right': 'Steer right',
  'hud.horn': 'Horn',
  'hud.drift': 'Drift',
  'hud.gas': 'Gas',
  'hud.brake': 'Brake and reverse',
  'hud.step': 'Step {n} of {of}: {text}',
  'hud.hintDeliver': 'Follow the arrow to the customer. Take bumps gently, and stop for a moment at the door.',
  'hud.hintLoad': 'Ride to the orange circle at the bakery and stop for a moment to load up.',
  'hud.hintAcceptTouch': 'Tap Accept on an order.',
  'hud.hintAcceptKey': 'Click Accept or press Enter.',

  // --- Orders and cargo ---
  'offer.race': '🏁 Race against Sjors · +{bonus}',
  'offer.tipLine': '+ tip · {sec}s',
  'offer.accept': 'Accept',
  'offer.full': 'Box is full',
  'offer.emptyFirst': 'Empty your box',
  'offer.decline': 'Decline',
  'cargo.pickup': 'Pick up at the bakery',
  'cargo.time': 'Time',
  'cargo.warm': 'Warm',
  'cargo.cold': 'Cold',
  'cargo.care': 'Intact',
  'target.load': 'Load up ({n})',

  // --- Receipt ---
  'receipt.late': '⏰ Late',
  'receipt.ontime': '✓ On time',
  'receipt.warm': '🔥 Warm {pct}%',
  'receipt.cold': '❄️ Cold {pct}%',
  'receipt.care': '🍰 Intact {pct}%',
  'receipt.combo': '🔥 Combo x{n}',
  'receipt.starsAria': '{n} out of 5 stars',

  // --- Start screen ---
  'start.tagline': 'On time, warm and in one piece.',
  'start.rank': 'Rank {rank} of {total}',
  'start.stars': '★ {stars}',
  'start.bestDay': 'Best day: {money}',
  'start.noBestDay': 'No best day yet',
  'start.nextRank': '{stars} to go until {title}: {districts}',
  'start.maxRank': 'Top rank: the whole city is yours.',
  'start.resume': '▶ Continue your shift',
  'start.resumeStatus': '{time} · {money} earned',
  'start.startFirst': 'Start your first shift',
  'start.start': 'Start your shift',
  'start.daily': "📅 Today's challenge",
  'start.dailyEveryone': 'Same orders for everyone',
  'start.dailyBest': 'Best today: {money}',
  'start.garage': '🛠️ Garage',
  'start.achievements': '🏅 Achievements',
  'start.freeride': '🛵 Free ride',
  'start.help': '❓ How to play',
  'start.settings': '⚙️ Settings',
  'start.install': '📲 Add to your home screen',
  'start.installHelp': "Tap Share and choose 'Add to Home Screen'.",

  // --- Pause ---
  'pause.title': 'Paused',
  'pause.subShift': 'The clock is stopped.',
  'pause.subFreeride': 'Free ride: no clock, no orders.',
  'pause.resume': 'Keep riding',
  'pause.stopShift': 'Stop shift',
  'pause.stopFreeride': 'Back to the menu',

  // --- End of shift ---
  'report.kicker': 'End of your shift',
  'report.title0': 'Better luck tomorrow!',
  'report.title1': 'Nicely done!',
  'report.title2': 'Good work!',
  'report.title3': 'Top day!',
  'report.titleNone': 'No deliveries today',
  'report.starsAria': '{n} out of 3 day stars',
  'report.record': '🏆 New record!',
  'report.earned': 'Earned',
  'report.delivered': 'Delivered',
  'report.average': 'Average',
  'report.bestCombo': 'Best combo',
  'report.starsGained': 'Stars gained',
  'report.missed': 'Missed or lost',
  'report.nextGoal': 'Next day star at {money}.',
  'report.allGoals': 'All three day stars earned!',
  'report.race': '🏁 Sjors races: {won} won, {lost} lost.',
  'report.dailyBest': "📅 Today's challenge: {money}. That's your best today!",
  'report.dailyOther': "📅 Today's challenge: {money}. Your best today is {best}.",
  'report.promotion': '⭐ Promoted to {title}! From now on you also deliver in {districts}.',
  'report.retry': 'Try again',
  'report.next': 'Next shift',
  'report.garage': '🛠️ Garage',
  'report.menu': 'Menu',

  // --- Sharing a score ---
  'report.share': '🔗 Share your score',
  'report.shareCopied': '✓ Link copied',
  'report.shareFailed': 'Sharing is not possible here',
  'report.challengeMet': "🎯 A friend's score ({goal}): beaten!",
  'report.challengeMiss': "🎯 A friend's score ({goal}): {left} to go.",
  'share.text': 'I earned {money} with {count} in Bak en Bezorg. Can you beat that?',
  'share.deliveries_one': '{n} delivery',
  'share.deliveries_other': '{n} deliveries',
  'share.title': 'Shared score',
  'share.intro': 'Someone got this in one shift. Can you beat it?',
  'share.introDaily': "Someone got this in the challenge of {date}. Can you beat it?",
  'share.dayStars': 'Day stars',
  'share.rank': 'Rank',
  'share.beat': 'Try to beat it',
  'share.playDaily': "Play today's challenge",
  'share.otherDay': "That challenge was from another day. The orders are different today, but you can still try to beat the score.",
  'share.hasSaved': 'You still have a shift open. Finish that first, then you can try to beat this score.',
  'share.close': 'Close',
  'share.invalidTitle': "This link doesn't work",
  'share.invalidText': 'The shared score is unreadable or damaged. Ask for a new link.',

  // --- Garage ---
  'garage.title': 'Garage',
  'garage.intro': 'You buy with euros, stars unlock things. Your box decides how many orders you carry at once.',
  'garage.vehicle': 'Vehicle',
  'garage.box': 'Box',
  'garage.boxOnScooter': 'Box (on the scooter)',
  'garage.wheels': 'Tyres',
  'garage.engine': 'Engine',
  'garage.paint': 'Colour',
  'garage.paintHint': 'You unlock new colours with stars.',
  'garage.chosen': 'Selected',
  'garage.choose': 'Select',
  'garage.needStars': '{stars} more needed.',
  'garage.needMoney': '{money} more needed.',
  'garage.lockedFrom': '{name}, from {stars}',
  'garage.capacity_one': '{n} order at a time',
  'garage.capacity_other': '{n} orders at a time',
  'garage.scooter': 'Scooter',
  'garage.scooterDesc': 'Nimble and fast, and can cut across pavements and grass.',
  'garage.vanDesc': '{capacity} and a cooling compartment, but on pavements and grass you ride at half speed.',
  'garage.noBox': 'No box',
  'garage.noBoxDesc': '{capacity}. Cold products stay the coldest.',
  'garage.smallBoxDesc': '{capacity}, keeps warm, absorbs shocks.',
  'garage.bigBoxDesc': '{capacity}, very warm, absorbs shocks well. Ice cream melts faster.',
  'garage.standard': 'Standard',
  'garage.standardWheelsDesc': 'Fine in dry weather.',
  'garage.sportWheelsDesc': 'More grip in rain, in puddles and on cobbles.',
  'garage.standardEngineDesc': 'Calm and predictable.',
  'garage.turboDesc': 'Higher top speed. Watch out for bumps.',

  // --- Upgrades and paints (economy.ts) ---
  'upgrade.box_kleine.name': 'Small box',
  'upgrade.box_kleine.desc': '2 orders at a time, keeps warm, absorbs shocks.',
  'upgrade.wheels_sport.name': 'Sport rims',
  'upgrade.wheels_sport.desc': 'More grip in rain and on cobbles.',
  'upgrade.engine_turbo.name': 'Turbo',
  'upgrade.engine_turbo.desc': 'Higher top speed.',
  'upgrade.box_grote.name': 'Large insulated box',
  'upgrade.box_grote.desc': '3 orders at a time, stays warm long, absorbs shocks well.',
  'upgrade.van.name': 'Delivery van',
  'upgrade.van.desc': '4 orders at a time and a cooling compartment, but slow over pavements and grass.',
  'paint.dc2626': 'Baker red',
  'paint.d97706': 'Amber',
  'paint.0284c7': 'Sky blue',
  'paint.059669': 'Emerald green',
  'paint.7c3aed': 'Violet',
  'paint.18181b': 'Matte black',

  // --- Ranks and districts ---
  'rank.1': "Baker's helper",
  'rank.2': 'Regular courier',
  'rank.3': 'Master courier',
  'district.dorpskern': 'Old village',
  'district.campus': 'Campus',
  'district.centrum': 'Centre',
  'district.haven': 'Harbour district',
  'district.oost': 'East',
  'district.industrie': 'Industrial',
  'district.zuid': 'South',

  // --- Products ---
  'product.koffie.name': 'Coffee & apple turnovers',
  'product.koffie.tip': 'Hurry: coffee cools down fast.',
  'product.ontbijt.name': 'Breakfast with croissants',
  'product.ontbijt.tip': 'Keep it warm and be on time.',
  'product.lunch.name': 'Lunch with sausage rolls',
  'product.lunch.tip': 'Big and warm: a good earner.',
  'product.brood.name': 'Bread for the lunch room',
  'product.brood.tip': 'Sturdy: only the time really counts.',
  'product.slagroomtaart.name': 'Whipped cream cake with fresh fruit',
  'product.slagroomtaart.tip': 'Fragile: take bumps gently.',
  'product.petitfours.name': 'Luxury petit fours',
  'product.petitfours.tip': 'Delicate sweets: every bump costs money.',
  'product.ijstaart.name': 'Ice cream cake',
  'product.ijstaart.tip': 'Melts fast. An insulated box makes it worse.',
  'product.bruidstaart.name': 'Wedding cake',
  'product.bruidstaart.tip': 'Fills your whole box. One knock and the party is over.',

  // --- Achievements ---
  'ach.title': 'Achievements',
  'ach.count': '{done} of {total}',
  'ach.got': '✓ earned',
  'ach.eerste_levering.name': 'First customer',
  'ach.eerste_levering.desc': 'Deliver your first order.',
  'ach.perfecte_taart.name': 'Not a dent',
  'ach.perfecte_taart.desc': 'Deliver a cake flawless and with 5 stars.',
  'ach.combo_5.name': 'On a roll',
  'ach.combo_5.desc': 'Reach a combo of 5 (4 or 5 stars five times in a row).',
  'ach.tien_op_een_dag.name': 'Busy day',
  'ach.tien_op_een_dag.desc': 'Deliver 10 orders in one shift.',
  'ach.volle_bak.name': 'Full box',
  'ach.volle_bak.desc': 'Deliver a full box of at least 3 orders, all on time.',
  'ach.bruidstaart.name': 'I do',
  'ach.bruidstaart.desc': 'Deliver a wedding cake with at least 4 stars.',
  'ach.sjors_verslagen.name': 'Faster than Sjors',
  'ach.sjors_verslagen.desc': 'Beat Sjors of Knus Expres to the door three times.',
  'ach.topdag.name': 'Top day',
  'ach.topdag.desc': 'Earn all three day stars in one shift.',
  'ach.foutloos.name': 'Velvet gloves',
  'ach.foutloos.desc': 'Deliver at least 6 orders in one shift without damage and without a fine.',
  'ach.schans.name': 'Over the canal',
  'ach.schans.desc': 'Jump over the raised drawbridge.',
  'ach.hele_stad.name': 'Knows the whole city',
  'ach.hele_stad.desc': 'Deliver in all seven districts.',
  'ach.dagelijks.name': 'Daily bread',
  'ach.dagelijks.desc': "Play today's challenge.",

  // --- Settings ---
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.languageAuto': 'Automatic',
  'settings.languageHint': "Automatic follows your device's language.",
  'settings.sound': 'Sound',
  'settings.soundMotor': 'Engine and weather',
  'settings.soundAlarm': 'Sirens and horns',
  'settings.soundEffects': 'Effects',
  'settings.levelLow': 'Soft',
  'settings.levelNormal': 'Normal',
  'settings.soundMixHint': 'Turn single sounds down or off. You only hear them when Sound is on.',
  'settings.theme': 'Day and night',
  'settings.themeDay': 'Day',
  'settings.themeNight': 'Night',
  'settings.themeAuto': 'Automatic',
  'settings.themeHint': 'Automatic follows your phone, and gets dark in the evening.',
  'settings.controls': 'Steering on a touch screen',
  'settings.controlsAria': 'Steering',
  'settings.controlsButtons': 'Buttons',
  'settings.controlsJoystick': 'Joystick',
  'settings.controlsThumb': 'One thumb',
  'settings.controlsHint': 'One thumb: drag where you want to go, the throttle is automatic.',
  'settings.assist': 'Steering assist',
  'settings.assistHint': 'Keeps you straight in the street when you are not steering.',
  'settings.minimap': 'Corner map',
  'settings.colorblind': 'Colour-blind friendly route',
  'settings.colorblindHint': 'Different colours, plus a dash, dots or symbol that show how much time you have left.',
  'settings.motion': 'Motion',
  'settings.motionAuto': 'Automatic',
  'settings.motionLess': 'Less',
  'settings.motionNormal': 'Normal',
  'settings.motionHint': 'Less: no shaking, flashes or moving pop-ups.',
  'settings.vibrate': 'Vibration',
  'settings.version': 'Version {version} · build {build}',
  'settings.versionNone': 'Version –',
  'settings.upToDate': 'You have the latest version.',
  'settings.updateReady': 'A new version is ready.',
  'settings.updateNow': 'Load new version',

  // --- How to play ---
  'help.title': 'How to play',
  'help.step1': '<b>Accept orders.</b> They come in during your shift. Your box decides how many you carry at once.',
  'help.step2': '<b>Load up at the bakery.</b> Ride to the orange circle and stop for a moment. From then on the customer\'s clock is running.',
  'help.step3': '<b>Deliver on time, warm and intact.</b> The ring around the customer shows your time. Bumps and crashes damage cakes.',
  'help.step4': '<b>Earn coins</b> and buy a bigger box, better tyres or the van in the garage.',
  'help.keyboard': 'Keyboard',
  'help.keys': 'Arrows or WASD to ride · Space drift · H horn · Enter accept · X decline · Esc pause',
  'help.understood': 'Got it',

  // --- Police and fines ---
  'fine.police': '👮 Police',
  'fine.title': 'Fine',
  'fine.total': 'Total',
  'fine.pay': 'Pay',
  'fine.contest': 'Explain (chance of half the fine)',
  'fine.rood_licht.title': 'Ran a red light',
  'fine.rood_licht.line1': 'Failing to stop for a red light',
  'fine.rood_licht.line2': 'Administration costs',
  'fine.rood_licht.quote': 'The speed camera caught you running a red light. That is dangerous for cyclists and pedestrians.',
  'fine.kat.title': 'Hit a cat',
  'fine.kat.titleNamed': 'Hit a cat: {cat}',
  'fine.kat.line1': 'Reckless riding',
  'fine.kat.line2': 'Animal ambulance',
  'fine.kat.quote': 'Cats always have right of way in this neighbourhood. Ride more calmly from now on.',
  'fine.neighbourCat': 'the neighbourhood cat',
  'fine.hitCat': '🚨 You hit {cat}! The police are on their way.',
  'fine.flash': '📸 Flashed at {place}: ran a red light!',
  'fine.halved': '👮 Good story: fine halved to €{amount}',
  'fine.notBelieved': "👮 The officer doesn't believe you: €{amount}",
  'fine.paid': '👮 Fine paid: €{amount}',

  // --- Messages during play ---
  'toast.achievement': '🏅 {name}! +{money}',
  'toast.firstDelivery': '🎉 First delivery! From now on more orders come in at once.',
  'toast.full': 'Your box is full ({n}). Deliver first, or buy a bigger box.',
  'toast.bridalAlone': 'A wedding cake travels alone: your box must be empty.',
  'toast.expired': 'This order has already expired.',
  'toast.raceStart': '🏁 Sjors wants the same customer! Whoever gets there first wins.',
  'toast.raceLost': '😤 Sjors got there first: you lost that order.',
  'toast.raceWon': '🏁 Beat Sjors! +{money}',
  'toast.loaded': 'Loaded: {items} — the clock is running!',
  'toast.promotion': '⭐ Promoted: {title}! New districts from your next shift.',
  'toast.shock': '💥 {reason}: −{pct}% intact',
  'toast.district': '📍 {name}',
  'toast.bridgeUp': '🌉 The bridge is going up: take a detour or wait{note}',
  'toast.bridgeDown': '🌉 The bridge is closed again',
  'toast.extraSeconds': ' (+{n} s added)',
  'shock.bump': 'Hit a bump too hard',
  'shock.collision': 'Crash',
  'shock.pion': 'Hit a cone',
  'shock.portier': 'Hit a doorman',
  'shock.auto': 'Hit a car',
  'shock.kat': 'Hit a cat',
  'shock.other': 'Shock',
  'float.stunt': '🛹 Ramp jump!',
  'float.overCanal': 'Over the canal!',
  'float.boost': '💨 Boost!',
  'float.slalom': '⚡ Traffic jam slalom: +12s',
  'float.raceLost': '🏁 Sjors wins!',
  'float.loaded': '🥖 Freshly loaded!',
  'float.reward': '{label} +{money}',

  // --- Events ---
  'event.bui.warn': 'In {sec} seconds it starts raining: warm food will cool down faster.',
  'event.bui.start': 'It is raining: ride calmly, the road is slippery.',
  'event.bui.end': 'The shower is over.',
  'event.mist.warn': 'In {sec} seconds fog rolls in.',
  'event.mist.start': 'Fog: you see less far, watch out for traffic.',
  'event.mist.end': 'The fog is lifting.',
  'event.markt.warn': 'In {sec} seconds {street} closes for the market.',
  'event.markt.start': '{street} is closed: take a detour.',
  'event.markt.end': 'The market is gone: the street is open again.',
  'event.markt.name': 'Market on {street}',
  'weather.sunny': '☀️ The sky is clearing',
  'weather.rain': '🌧️ Rain: slippery road, warm food cools down faster',
  'weather.mist': '🌫️ Fog: less visibility, ride calmly',

  // --- Guided first minute ---
  'guide.deliverNear': 'Stop for a moment at the door: the customer is collecting the order.',
  'guide.deliver': 'Follow the arrow to the customer and take bumps gently.',
  'guide.loadNear': 'Keep still for a moment: your box is being loaded.',
  'guide.load': 'Ride to the orange circle at the bakery.',
  'guide.acceptTouch': 'Tap Accept on the order.',
  'guide.acceptKey': 'Click Accept or press Enter.',

  // --- Text in the world (canvas) ---
  'world.speedCamera': 'SPEED ALERT: Speed camera in {m}m',
  'world.speedWarning': '⚠️ SLOW DOWN! A fine is coming!',
  'world.location': 'Location: {place}',
  'world.detourLeft': '◀ DETOUR',
  'world.detourRight': 'DETOUR ▶',
  'world.jam': '⚠️ JAM • 30 KM/H',
  'world.slalomActive': '⚡ SLALOM ACTIVE!',
  'world.slalom': 'SLALOM FOR TIME',
  'world.rival': 'RIVAL DELIVERY SERVICE',
  'world.policeChase': '🚨 POLICE (Pursuit!)',
  'world.police': '👮 Police',
  'world.policeCaps': 'POLICE',
  'world.market': '🛒 MARKET',
  'world.speedCameraPole': '📸 SPEED CAMERA',
  'world.loadingZone': 'LOADING',
  'world.bridgeLifted': '🛹 RAMP OPEN: DARE THE JUMP AT FULL THROTTLE!',
  'world.bridgeLights': '⚠️ BRIDGE LIGHTS ON FOR SHIPPING',
  'world.bakeryTagline': 'Artisan Bakery & Headquarters',
  'world.grain': 'GRAIN',
  'world.bakery': '🥖 BAKERY',
  'cat.crossing': ' 🚸 (Crossing)',

  // --- Speech bubbles of cats, cars and police ---
  'chat.cat.scared': '🙀 "Meow!"',
  'chat.cat.wait1': '👀 *Waiting for the car...*',
  'chat.cat.wait2': '🚗 *Letting the car pass*',
  'chat.cat.wait3': '🐾 *Looking left and right*',
  'chat.cat.wait4': '🛑 *Waiting politely at the road*',
  'chat.cat.cross': '✨ *The road is clear! Crossing...*',
  'chat.cat.crossed': '🐾 *Crossed safely!*',
  'chat.cat.sniff': '🐾 *Sniffing a flower pot*',
  'chat.cat.wash': '😺 *Washing its face*',
  'chat.cat.purr': '💤 *Purring happily*',
  'chat.cat.stretch': '🐾 *Having a good stretch*',
  'chat.rival': '⚡ Sjors (Knus Expres): "Step on it, Knus & Krokant will never catch us!"',
  'chat.car1': '👍 "I\'m pulling over!"',
  'chat.car2': '🥐 "Good luck with the delivery!"',
  'chat.car3': '🚗💨 "Go right ahead, courier!"',
  'chat.car4': '🥖 "Nice work, Knus & Krokant!"',
  'chat.car5': '👋 "Have a good ride!"',
  'chat.police1': '🚨 "HALT! Knusseveld Police! Stop your scooter!"',
  'chat.police2': '📢 "Pull over! Stop immediately!"',
  'chat.police3': '👮‍♂️ "Stop in the name of the law!"',
  'chat.police4': '🚨 "Collision with a village cat reported! Stop!"',

  // --- Updates and offline ---
  'update.title': 'New version available',
  'update.text': 'Load it when it suits you; your progress is kept.',
  'update.reload': 'Refresh',
  'update.later': 'Later',
  'analytics.title': 'Anonymous statistics',
  'analytics.ask': 'May I anonymously track how the game is used? It helps me improve it.',
  'analytics.desc': 'Anonymous visits and play time, to improve the game. Your progress stays on your device.',
  'analytics.allow': 'Allow',
  'analytics.deny': 'Not now',
  'offline.banner': 'Offline: the game keeps working, your progress is kept.',
  'offline.back': 'Back online.',
};
